/**
 * Copyright (c) 2025 George Nassef, Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  Box,
  Divider,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import MovieIcon from '@mui/icons-material/Movie';
import apiService from '../services/api';

const ScriptDetailPage: React.FC = () => {
  const { scriptId } = useParams<{ scriptId: string }>();
  const navigate = useNavigate();
  const [script, setScript] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchScript = async () => {
      if (!scriptId) return;
      
      setIsLoading(true);
      setError(null);
      
      // Check if we have a UUID format ID in session storage (for recovery)
      const storedScriptId = sessionStorage.getItem('last_script_id');
      const currentId = scriptId;
      
      console.log(`Loading script: ${currentId}, stored ID: ${storedScriptId || 'none'}`);
      
      try {
        // Try to get the script with the current ID
        try {
          const scriptData = await apiService.getScript(currentId);
          console.log('Successfully loaded script with ID:', currentId);
          setScript(scriptData);
          setEditedContent(scriptData.content);
          return; // Exit if successful
        } catch (err) {
          console.warn(`Error loading script with ID ${currentId}:`, err);
          
          // If we have a stored UUID and current ID is not the same, try with stored ID
          if (storedScriptId && storedScriptId !== currentId) {
            console.log('Trying stored script ID:', storedScriptId);
            try {
              const storedScriptData = await apiService.getScript(storedScriptId);
              console.log('Successfully loaded script with stored ID');
              setScript(storedScriptData);
              setEditedContent(storedScriptData.content);
              
              // Update URL to reflect the correct ID without reloading
              window.history.replaceState(
                null, 
                '', 
                `/scripts/${storedScriptId}`
              );
              return; // Exit if successful
            } catch (storedIdErr) {
              console.warn('Error loading script with stored ID:', storedIdErr);
              // Continue to error handling
            }
          }
          
          // If both failed, try a last effort with "latest" project from the server
          try {
            console.log('Getting latest project as a fallback');
            const latestProject = await apiService.getLatestProject();
            
            if (latestProject) {
              console.log('Using latest project as fallback');
              
              // Convert Project to Script format using available properties
              const fallbackScript = {
                id: `script-${latestProject.id}`, // Prefix with script- for consistency
                title: latestProject.title,
                // Use a placeholder content if project doesn't have script content
                content: (latestProject as any).script_content || (latestProject as any).content || 
                         'Script content not yet generated for this project.',
                created_at: latestProject.created_at,
                updated_at: latestProject.updated_at,
                scene_setting: latestProject.setting || '',
                characters: latestProject.characters || {}
              };
              
              setScript(fallbackScript);
              setEditedContent(fallbackScript.content);
              return; // Exit if successful
            }
          } catch (latestErr) {
            console.warn('Error getting latest project:', latestErr);
          }
          
          // If all else fails, show error
          throw new Error('Unable to load any script content');
        }
      } catch (finalErr) {
        console.error('All script loading attempts failed:', finalErr);
        setError('Failed to load the script. The script may not exist or may not contain any content.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchScript();
  }, [scriptId]);

  const handleSaveEdit = async () => {
    if (!scriptId) return;
    
    setIsLoading(true);
    try {
      const updatedScript = await apiService.updateScript(scriptId, editedContent);
      setScript(updatedScript);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating script:', err);
      setError('Failed to save the script. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateVideo = () => {
    // For now, show a simple alert instead of navigating
    alert('Video generation is not available in this version.');
    // Uncomment below to enable video generation once it's ready
    // navigate(`/generate-video/${scriptId}`);
  };

  // Format the script content for display with colors
  const renderFormattedScript = () => {
    if (!script?.content) return null;
    
    // Create style sheet for script formatting with white/shades of white text
    const style = `
      <style>
        .scene-heading { color: #FFFFFF; font-weight: bold; margin-top: 16px; margin-bottom: 8px; }
        .action { color: #E8E8E8; margin-bottom: 8px; }
        .character { color: #FFFFFF; font-weight: bold; margin-top: 16px; margin-bottom: 0; }
        .parenthetical { color: #D0D0D0; font-style: italic; margin-top: 2px; margin-bottom: 2px; }
        .dialogue { color: #F0F0F0; margin-bottom: 16px; margin-left: 16px; }
        .transition { color: #FFFFFF; font-weight: bold; margin: 16px 0; }
        .camera { color: #E0E0E0; font-style: italic; margin-bottom: 8px; }
      </style>
    `;
    
    // Apply basic formatting for scene headings, characters, and dialogue
    let formatted = script.content
      // Format scene headings
      .replace(/(INT\.|EXT\.)(.*?)(\n|$)/g, '<div class="scene-heading">$1$2</div>')
      // Format character names
      .replace(/^([A-Z][A-Z\s]+)(\s*\(.*?\))?(\n|$)/gm, '<div class="character">$1$2</div>')
      // Format parentheticals
      .replace(/^\((.*?)\)(\n|$)/gm, '<div class="parenthetical">($1)</div>')
      // Format camera directions
      .replace(/\[Camera:([^\]]+)\]/g, '<div class="camera">[Camera:$1]</div>')
      // Format transitions
      .replace(/^(FADE IN:|FADE OUT:|CUT TO:|DISSOLVE TO:)(.*?)(\n|$)/gm, '<div class="transition">$1$2</div>')
      // Format dialogue (any line that isn't already formatted)
      .replace(/^(?!<div class)(.*?)(\n|$)/gm, '<div class="dialogue">$1</div>');
    
    return <div dangerouslySetInnerHTML={{ __html: style + formatted }} />;
  };

  if (isLoading) {
    return (
      <Box className="flex justify-center items-center h-64">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !script) {
    return (
      <Box className="text-center p-6">
        <Paper elevation={2} className="p-8 mx-auto max-w-md">
          <Typography variant="h5" color="error" className="mb-4">
            {error || "Script not found"}
          </Typography>
          
          <Alert severity="info" className="mb-6">
            It looks like you don't have any scripts available. This might be because:
            <ul className="list-disc pl-6 mt-2">
              <li>You're trying to access a script that no longer exists</li>
              <li>The script hasn't been generated yet</li>
              <li>The database has been recently cleared</li>
            </ul>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Let's create a new script to get started!
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={() => navigate('/create-script')}
            className="mt-4"
          >
            Create New Script
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <div>
      <Box className="flex justify-between items-center mb-6">
        <Typography variant="h4" component="h1" className="teen-heading">
          {script.title}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(!isEditing)}
            className="mr-2"
          >
            {isEditing ? 'Cancel' : 'Edit Script'}
          </Button>
          {isEditing ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
            >
              Save Changes
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<MovieIcon />}
              onClick={handleGenerateVideo}
              className="teen-button-primary"
              disabled
              title="Video generation is not available in this version"
            >
              Generate Video
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Cards in horizontal layout */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Script Details
              </Typography>
              <Divider className="mb-3" />
              <Typography variant="body2" className="mb-2">
                <strong>Setting:</strong> {script.scene_setting}
              </Typography>
              <Typography variant="body2" className="mb-2">
                <strong>Created:</strong> {new Date(script.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Last Updated:</strong> {new Date(script.updated_at).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Characters
              </Typography>
              <Divider className="mb-3" />
              {script.characters && Object.keys(script.characters).length > 0 ? (
                Object.entries(script.characters).map(([name, details]: [string, any]) => (
                  <Box key={name} className="mb-3">
                    <Typography variant="subtitle1" className="font-bold">
                      {name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Age:</strong> {details.age}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Description:</strong> {details.description}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No character details available for this script.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Widened Script Content */}
      <Grid container>
        <Grid item xs={12}>
          <Paper className="p-6">
            <Typography variant="h6" className="mb-4">
              Script Content
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={20}
                variant="outlined"
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
              />
            ) : (
              <Box 
                className="font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 p-4 rounded"
                style={{ maxHeight: '600px', overflowY: 'auto' }}
              >
                {renderFormattedScript()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default ScriptDetailPage;
